<template>
  <div class="edit_profile">
    <!-- 头像区域 -->
    <div class="edit_profile__avatar_wrap">
      <div class="edit_profile__avatar" @click="onPickAvatar">
        <img :src="displayAvatar" alt="" />
      </div>
      <span class="edit_profile__avatar_link" @click="onPickAvatar">点击更换头像</span>
    </div>

    <!-- 当前昵称 -->
    <div class="edit_profile__field edit_profile__field--current">
      <div class="edit_profile__label">当前昵称</div>
      <div class="edit_profile__readonly">
        {{ currentNickname || '—' }}
      </div>
    </div>

    <!-- 新昵称 -->
    <div class="edit_profile__field edit_profile__field--new">
      <div class="edit_profile__label edit_profile__label--strong">新昵称</div>
      <div class="edit_profile__input_wrap">
        <input
          v-model="newNickname"
          type="text"
          maxlength="12"
          class="edit_profile__input"
          :placeholder="currentNickname || '请输入新昵称'"
        />
      </div>
    </div>

    <!-- 保存按钮 -->
    <div class="edit_profile__footer">
      <van-button
        block
        round
        :loading="submitting"
        :disabled="saveDisabled"
        :color="saveDisabled ? '#d9d9d9' : '#ff7a00'"
        class="edit_profile__save_btn"
        @click="onSave"
      >
        保存
      </van-button>
    </div>

    <input
      ref="albumInput"
      type="file"
      accept="image/jpeg,image/png"
      class="edit_profile__file"
      @change="onPickFile"
    />
  </div>
</template>

<script>
import { Button, Toast } from 'vant';
import { getUserCenter, modifyUserInfo, uploadForumFile } from '@/api/forum';
import { FORUM_DEFAULT_AVATAR } from './constants';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';

const MAX_AVATAR_SIZE = 10 * 1024 * 1024;
const NICKNAME_VALID = /[\u4E00-\u9FA5A-Za-z0-9_]/g;

export default {
  name: 'ForumEditProfile',
  mixins: [trackingMixin],
  components: {
    [Button.name]: Button,
  },
  data() {
    return {
      currentAvatar: '',
      currentNickname: '',
      newAvatar: '',
      newNickname: '',
      avatarUploading: false,
      submitting: false,
      skipInitialActivated: false,
    };
  },
  computed: {
    displayAvatar() {
      return this.newAvatar || this.currentAvatar || FORUM_DEFAULT_AVATAR;
    },
    nicknameLen() {
      return Array.from(this.newNickname).length;
    },
    nicknameChanged() {
      return !!this.newNickname.trim() && this.newNickname.trim() !== this.currentNickname;
    },
    hasChange() {
      return this.nicknameChanged || !!this.newAvatar;
    },
    saveDisabled() {
      if (this.submitting || this.avatarUploading) return true;
      if (!this.hasChange) return true;
      if (this.newNickname && (this.nicknameLen < 1 || this.nicknameLen > 12)) return true;
      return false;
    },
  },
  watch: {
    newNickname(val) {
      const filtered = (val.match(NICKNAME_VALID) || []).join('');
      const codePoints = Array.from(filtered);
      const next = codePoints.length > 12 ? codePoints.slice(0, 12).join('') : filtered;
      if (next !== val) {
        this.newNickname = next;
        return;
      }
      if (next && this.nicknameLen > 12) {
        Toast('昵称长度应在 1-12 个字符之间');
      }
    },
  },
  activated() {
    if (this.skipInitialActivated) {
      this.skipInitialActivated = false;
      return;
    }
    this.fetchMe();
    this.trackPageShow();
  },
  created() {
    this.skipInitialActivated = true;
    this.fetchMe();
    this.trackPageShow();
  },
  methods: {
    trackPageShow() {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_page_show, {
        page_type: '昵称头像编辑',
      });
    },
    async fetchMe() {
      const res = await getUserCenter();
      const data = (res && res.data) || {};
      if (data.nickname) this.currentNickname = data.nickname;
      if (data.avatar) this.currentAvatar = data.avatar;
    },
    onPickAvatar() {
      if (this.avatarUploading) return;
      this.$refs.albumInput.click();
    },
    async onPickFile(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      if (!/^image\/(jpeg|png)$/i.test(file.type)) {
        Toast('请选择 JPG 或 PNG 格式图片');
        return;
      }
      if (file.size > MAX_AVATAR_SIZE) {
        Toast('图片大小不能超过 10MB');
        return;
      }
      this.avatarUploading = true;
      const loading = Toast.loading({
        message: '头像上传中...',
        forbidClick: true,
        duration: 0,
      });
      try {
        const formData = new FormData();
        formData.append('file', file, file.name);
        const res = await uploadForumFile(formData);
        const url = res && res.data;
        if (url) {
          this.newAvatar = url;
          Toast.success('头像修改成功');
        }
      } catch (error) {
        // 接口失败提示由 axios 拦截器统一处理
      } finally {
        loading.clear();
        this.avatarUploading = false;
      }
    },
    async onSave() {
      if (this.saveDisabled) return;
      if (this.newNickname && (this.nicknameLen < 1 || this.nicknameLen > 12)) {
        Toast('昵称长度应在 1-12 个字符之间');
        return;
      }
      const payload = {};
      if (this.newAvatar) payload.avatar = this.newAvatar;
      if (this.nicknameChanged) payload.nickname = this.newNickname.trim();

      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_user_center_save_button, {
        page_type: '昵称头像编辑',
        nick_name_before: this.currentNickname || '',
        nick_name_after: payload.nickname || this.currentNickname || '',
      });

      this.submitting = true;
      const loading = Toast.loading({
        message: '保存中...',
        forbidClick: true,
        duration: 0,
      });
      try {
        const res = await modifyUserInfo(payload);
        const ok = !!(
          res
          && (res.success === true
            || (res.data === true)
            || res.code === 0
            || res.code === 200)
        );
        if (!ok) {
          return;
        }
        Toast.success('保存成功');
        if (payload.avatar) this.currentAvatar = payload.avatar;
        if (payload.nickname) this.currentNickname = payload.nickname;
        this.newAvatar = '';
        this.newNickname = '';
        setTimeout(() => {
          this.$router.back();
        }, 600);
      } catch (err) {
        // 接口失败提示由 axios 拦截器统一处理
      } finally {
        loading.clear();
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.edit_profile {
  min-height: 100vh;
  background: #fff;
}

.edit_profile__file {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.edit_profile__avatar_wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 14px;
  padding-bottom: 12px;
}

.edit_profile__avatar {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
  flex-shrink: 0;
}

.edit_profile__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.edit_profile__avatar_placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 24px;
}

.edit_profile__avatar_link {
  margin-top: 10px;
  color: #0249ff;
  font-size: 14px;
}

.edit_profile__field {
  padding: 0 16px;
}

.edit_profile__field--current {
  margin-top: 8px;
}

.edit_profile__field--new {
  margin-top: 14px;
}

.edit_profile__label {
  margin-bottom: 8px;
  color: #9ca3af;
  font-size: 14px;
}

.edit_profile__label--strong {
  color: #111827;
  font-weight: 600;
}

.edit_profile__readonly {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  border-radius: 24px;
  color: #9ca3af;
  font-size: 15px;
  background: #f7f7f7;
}

.edit_profile__input_wrap {
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border-radius: 24px;
  background: #f7f7f7;
}

.edit_profile__input {
  width: 100%;
  border: 0;
  outline: none;
  color: #111827;
  font-size: 15px;
  text-align: center;
  background: transparent;
}

.edit_profile__input::placeholder {
  color: #d1d5db;
}

.edit_profile__footer {
  margin-top: 64px;
  padding: 0 16px;
}

.edit_profile__save_btn {
  width: 100%;
  height: 48px;
  border: none;
}
</style>
